import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatusBadge, PriorityBadge } from '../components/StatusBadge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  EnhancedTable,
  StickyTableHeader,
  SortableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
  SavedFiltersDropdown,
  FilterChips,
} from '../components/ui/enhanced-table';
import { useTableSort, useSavedFilters } from '../hooks/useTableEnhancements';
import { Plus, FileText, Search, X, AlertTriangle, User, Zap, Camera, Video, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import VoiceRecorder from '../components/VoiceRecorder';

const MAX_FILES = 5;
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_IMAGE_SIZE = 15 * 1024 * 1024; // 15MB
const ACCEPTED_TYPES = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/x-msvideo,video/webm';

const TABS = [
  { key: 'all', filter: {} },
  { key: 'myRequests', filter: { my_requests: true } },
  { key: 'open', filter: { status: 'NEW' } },
  { key: 'highPriority', filter: { priority: 'HIGH,CRITICAL' } },
  { key: 'closed', filter: { status: 'CLOSED,REJECTED' } },
];

export default function WorkRequestsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [requests, setRequests] = useState([]);
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');

  // Filters
  const [fBranch, setFBranch] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fPriority, setFPriority] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Sorting with persistence
  const { sortField, sortDir, toggleSort, sortData } = useTableSort('wr_page', 'created_at', 'desc');

  // Saved filters
  const { savedFilters, saveCurrentFilters, saveFilterPreset, deleteFilterPreset, loadCurrentFilters } = useSavedFilters('wr_page');

  // Form
  const [form, setForm] = useState({ branch_id: '', room_id: '', stage: '', asset_id: '', priority: 'MEDIUM', description: '' });
  const [mediaFiles, setMediaFiles] = useState([]);
  const [voiceFile, setVoiceFile] = useState(null);
  const [formRooms, setFormRooms] = useState([]);
  const [formStages, setFormStages] = useState([]);
  const [formAssets, setFormAssets] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  // Load saved filters on mount
  useEffect(() => {
    const saved = loadCurrentFilters();
    if (saved) {
      if (saved.branch) setFBranch(saved.branch);
      if (saved.status) setFStatus(saved.status);
      if (saved.priority) setFPriority(saved.priority);
      if (saved.tab) setActiveTab(saved.tab);
    }
  }, []);

  // Save current filters when they change
  useEffect(() => {
    saveCurrentFilters({ branch: fBranch, status: fStatus, priority: fPriority, tab: activeTab });
  }, [fBranch, fStatus, fPriority, activeTab, saveCurrentFilters]);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadRequests(); }, [fBranch, activeTab]);

  const loadData = async () => {
    try {
      const [branchRes, roomRes, assetRes] = await Promise.all([
        api.get('/branches'), api.get('/rooms'), api.get('/assets'),
      ]);
      setBranches(branchRes.data);
      setRooms(roomRes.data);
      setAssets(assetRes.data);
      if (branchRes.data.length === 1) {
        setForm(f => ({ ...f, branch_id: branchRes.data[0].id }));
      }
    } catch (err) { toast.error(t('toast.error')); }
  };

  const loadRequests = async () => {
    try {
      const params = {};
      if (fBranch !== 'all') params.branch_id = fBranch;
      
      const tabConfig = TABS.find(t => t.key === activeTab);
      if (tabConfig?.filter) {
        Object.entries(tabConfig.filter).forEach(([key, value]) => {
          if (typeof value === 'string' && value.includes(',')) {
            params[key] = value.split(',')[0];
          } else {
            params[key] = value;
          }
        });
      }
      
      const { data } = await api.get('/work-requests', { params });
      setRequests(data);
    } catch (err) { toast.error(t('toast.error')); }
    finally { setLoading(false); }
  };

  const handleBranchChange = (branchId) => {
    setForm(f => ({ ...f, branch_id: branchId, room_id: '', stage: '', asset_id: '' }));
    setFormStages([]);
    setFormAssets([]);
    setFormRooms([]);
  };

  // Fetch rooms when branch changes
  useEffect(() => {
    if (!form.branch_id) { setFormRooms([]); return; }
    let cancelled = false;
    api.get('/rooms', { params: { branch_id: form.branch_id } })
      .then(res => { if (!cancelled) setFormRooms(res.data); })
      .catch(() => { if (!cancelled) setFormRooms([]); });
    api.get('/assets', { params: { branch_id: form.branch_id } })
      .then(res => { if (!cancelled) setFormAssets(res.data); })
      .catch(() => { if (!cancelled) setFormAssets([]); });
    return () => { cancelled = true; };
  }, [form.branch_id]);

  const handleRoomChange = (roomId) => {
    setForm(f => ({ ...f, room_id: roomId, stage: '', asset_id: '' }));
    setFormStages([]);
  };

  // Fetch stages + filter assets when room changes
  useEffect(() => {
    if (!form.room_id || !form.branch_id) { setFormStages([]); return; }
    let cancelled = false;
    api.get('/stages', { params: { branch_id: form.branch_id, room_id: form.room_id } })
      .then(res => { if (!cancelled) setFormStages(res.data); })
      .catch(() => { if (!cancelled) setFormStages([]); });
    api.get('/assets', { params: { branch_id: form.branch_id, room_id: form.room_id } })
      .then(res => { if (!cancelled) setFormAssets(res.data); })
      .catch(() => { if (!cancelled) setFormAssets([]); });
    return () => { cancelled = true; };
  }, [form.branch_id, form.room_id]);

  const handleStageChange = (stage) => {
    setForm(f => ({ ...f, stage, asset_id: '' }));
  };

  // Filter assets by stage for the dropdown
  const filteredAssets = form.stage
    ? formAssets.filter(a => a.stage === form.stage)
    : formAssets;

  const assetDisplayName = (a) => {
    const isAr = i18n.language === 'ar';
    return (isAr && a.name_ar) ? a.name_ar : a.name;
  };

  const handleMediaSelect = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const remaining = MAX_FILES - mediaFiles.length;
    if (remaining <= 0) { toast.error(t('workRequest.maxFilesReached')); return; }

    const valid = [];
    for (const f of newFiles.slice(0, remaining)) {
      const isVideo = f.type.startsWith('video/');
      const isImage = f.type.startsWith('image/');
      if (!isVideo && !isImage) { toast.error(`${f.name}: ${t('workRequest.onlyImagesVideos')}`); continue; }
      const limit = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
      if (f.size > limit) { toast.error(`${f.name}: ${t('workRequest.fileTooLarge', { max: isVideo ? '50MB' : '15MB' })}`); continue; }
      valid.push(f);
    }
    setMediaFiles(prev => [...prev, ...valid]);
    e.target.value = '';
  };

  const removeMedia = (index) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.branch_id) { toast.error('Branch is required'); return; }
    if (!form.room_id) { toast.error('Room is required'); return; }
    if (!form.stage) { toast.error('Stage is required'); return; }
    if (!form.asset_id) { toast.error('Asset is required'); return; }
    if (mediaFiles.length === 0) { toast.error(t('workRequest.mediaRequired')); return; }
    setSubmitting(true);
    try {
      // 1. Create the work request (title auto-generated from asset name on backend)
      const { data: wr } = await api.post('/work-requests', {
        branch_id: form.branch_id,
        room_id: form.room_id || null,
        asset_id: form.asset_id || null,
        stage: form.stage || null,
        priority: form.priority,
        description: form.description || '',
      });
      const wrId = wr.id;

      // 2. Upload voice note as attachment (if recorded)
      if (voiceFile) {
        setUploadProgress('Uploading voice note...');
        const fd = new FormData();
        fd.append('file', voiceFile);
        fd.append('entity_type', 'work_request');
        fd.append('entity_id', wrId);
        await api.post('/attachments/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      // 3. Upload each media file
      for (let i = 0; i < mediaFiles.length; i++) {
        setUploadProgress(t('workRequest.uploadingFile', { current: i + 1, total: mediaFiles.length }));
        const fd = new FormData();
        fd.append('file', mediaFiles[i]);
        fd.append('entity_type', 'work_request');
        fd.append('entity_id', wrId);
        await api.post('/attachments/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      toast.success(t('toast.saved'));
      setDialogOpen(false);
      setForm({ branch_id: '', room_id: '', stage: '', asset_id: '', priority: 'MEDIUM', description: '' });
      setMediaFiles([]);
      setVoiceFile(null);
      setUploadProgress('');
      loadRequests();
    } catch (err) {
      const data = err.response?.data;
      if (data && data.code === 'DUPLICATE_ACTIVE_WO') {
        const d = data.details || {};
        const dateStr = d.createdAt ? new Date(d.createdAt).toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
        const msg = i18n.language === 'ar'
          ? `هذا الأصل لديه بلاغ صيانة نشط تم تسجيله بواسطة ${d.requesterName || ''} بتاريخ ${dateStr}. الرجاء متابعة البلاغ الحالي.`
          : `This asset already has an active maintenance task created by ${d.requesterName || 'someone'} on ${dateStr}. Please follow the existing task.`;
        toast.error(msg, { duration: 8000 });
      } else {
        toast.error(data?.message || data?.detail || t('toast.error'));
      }
    }
    finally { setSubmitting(false); setUploadProgress(''); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFStatus('all');
    setFPriority('all');
    setSearchParams({ tab });
  };

  const clearFilters = () => {
    setFBranch('all');
    setFStatus('all');
    setFPriority('all');
    setSearchQuery('');
  };

  const applyFilterPreset = (filters) => {
    if (filters.branch) setFBranch(filters.branch);
    if (filters.status) setFStatus(filters.status);
    if (filters.priority) setFPriority(filters.priority);
    if (filters.tab) setActiveTab(filters.tab);
    toast.success(t('filters.applied'));
  };

  // Filter and sort data locally
  const filteredRequests = useMemo(() => {
    let result = [...requests];
    
    if (fStatus !== 'all') {
      result = result.filter(r => r.status === fStatus);
    }
    if (fPriority !== 'all') {
      result = result.filter(r => r.priority === fPriority);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => 
        r.title?.toLowerCase().includes(q) || 
        r.id?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
      );
    }
    
    if (activeTab === 'closed') {
      result = result.filter(r => ['CLOSED', 'REJECTED', 'CONVERTED_TO_WO'].includes(r.status));
    }
    if (activeTab === 'highPriority') {
      result = result.filter(r => ['HIGH', 'CRITICAL'].includes(r.priority));
    }
    
    return sortData(result);
  }, [requests, fStatus, fPriority, searchQuery, activeTab, sortData]);

  // Tab counts
  const tabCounts = useMemo(() => {
    const counts = {};
    counts.all = requests.length;
    counts.myRequests = requests.filter(r => r.created_by === user?.id).length;
    counts.open = requests.filter(r => r.status === 'NEW').length;
    counts.highPriority = requests.filter(r => ['HIGH', 'CRITICAL'].includes(r.priority)).length;
    counts.closed = requests.filter(r => ['CLOSED', 'REJECTED', 'CONVERTED_TO_WO'].includes(r.status)).length;
    return counts;
  }, [requests, user]);

  // Quick filter chips configuration
  const filterChips = useMemo(() => [
    {
      key: 'open',
      label: t('chips.open'),
      icon: FileText,
      count: tabCounts.open,
      isActive: () => activeTab === 'open' || fStatus === 'NEW',
      onActivate: () => handleTabChange('open'),
      onDeactivate: () => handleTabChange('all'),
    },
    {
      key: 'highPriority',
      label: t('chips.highPriority'),
      icon: AlertTriangle,
      count: tabCounts.highPriority,
      countVariant: 'warning',
      isActive: () => activeTab === 'highPriority',
      onActivate: () => handleTabChange('highPriority'),
      onDeactivate: () => handleTabChange('all'),
    },
    {
      key: 'myRequests',
      label: t('chips.myRequests'),
      icon: User,
      count: tabCounts.myRequests,
      isActive: () => activeTab === 'myRequests',
      onActivate: () => handleTabChange('myRequests'),
      onDeactivate: () => handleTabChange('all'),
    },
  ], [t, tabCounts, activeTab, fStatus]);

  const activeChips = filterChips.filter(chip => chip.isActive());

  const hasActiveFilters = fBranch !== 'all' || fStatus !== 'all' || fPriority !== 'all' || searchQuery;

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div data-testid="work-requests-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{t('workRequest.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{filteredRequests.length} {t('workRequest.title').toLowerCase()}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <SavedFiltersDropdown
            savedFilters={savedFilters}
            onSave={saveFilterPreset}
            onApply={applyFilterPreset}
            onDelete={deleteFilterPreset}
            currentFilters={{ branch: fBranch, status: fStatus, priority: fPriority, tab: activeTab }}
            t={t}
          />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="new-request-btn">
                <Plus className="w-4 h-4 me-2" /> {t('workRequest.newRequest')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('workRequest.createRequest')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('common.branch')} *</Label>
                    <Select value={form.branch_id} onValueChange={handleBranchChange} required data-testid="wr-branch-select">
                      <SelectTrigger><SelectValue placeholder={t('workRequest.selectBranch')} /></SelectTrigger>
                      <SelectContent>
                        {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('workRequest.priority')}</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm(f => ({ ...f, priority: v }))} data-testid="wr-priority-select">
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="LOW">{t('workRequest.low')}</SelectItem>
                        <SelectItem value="MEDIUM">{t('workRequest.medium')}</SelectItem>
                        <SelectItem value="HIGH">{t('workRequest.high')}</SelectItem>
                        <SelectItem value="CRITICAL">{t('workRequest.critical')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('common.room')} *</Label>
                    <Select key={`room-${form.branch_id}-${formRooms.length}`} value={form.room_id || 'none'} onValueChange={(v) => handleRoomChange(v === 'none' ? '' : v)} disabled={!form.branch_id} data-testid="wr-room-select">
                      <SelectTrigger><SelectValue placeholder={t('workRequest.selectRoom')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('workRequest.selectRoom')}</SelectItem>
                        {formRooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Stage *</Label>
                    <Select key={`stage-${form.room_id}-${formStages.length}`} value={form.stage || 'none'} onValueChange={(v) => handleStageChange(v === 'none' ? '' : v)} disabled={!form.room_id} data-testid="wr-stage-select">
                      <SelectTrigger><SelectValue placeholder="Select Stage" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select Stage</SelectItem>
                        {formStages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {form.room_id && formStages.length === 0 && (
                      <p className="text-[10px] text-muted-foreground">No stages found. Type stage manually below.</p>
                    )}
                  </div>
                </div>
                {/* Manual stage input when no stages available */}
                {form.room_id && formStages.length === 0 && (
                  <div className="space-y-2">
                    <Label>Stage (type manually) *</Label>
                    <Input value={form.stage} onChange={(e) => setForm(f => ({ ...f, stage: e.target.value }))} placeholder="e.g. Room 1, الغرفه الاولى" data-testid="wr-stage-input" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>{t('common.asset')} *</Label>
                  <Select key={`asset-${form.stage}-${filteredAssets.length}`} value={form.asset_id || 'none'} onValueChange={(v) => setForm(f => ({ ...f, asset_id: v === 'none' ? '' : v }))} disabled={!form.branch_id} data-testid="wr-asset-select">
                    <SelectTrigger><SelectValue placeholder={t('workRequest.selectAsset')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('workRequest.selectAsset')}</SelectItem>
                      {filteredAssets.map(a => <SelectItem key={a.id} value={a.id}>{assetDisplayName(a)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('common.description')}</Label>
                  <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t('workRequest.enterDescription')} rows={3} data-testid="wr-description-input" />
                  <VoiceRecorder
                    onRecordingComplete={(file) => setVoiceFile(file)}
                    onRecordingRemove={() => setVoiceFile(null)}
                  />
                </div>

                {/* Mandatory Media Upload */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    {t('workRequest.evidence')} *
                    <span className="text-xs text-muted-foreground font-normal">({mediaFiles.length}/{MAX_FILES})</span>
                  </Label>
                  {mediaFiles.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {mediaFiles.map((f, i) => (
                        <div key={i} className="relative group rounded-lg border border-border overflow-hidden bg-muted/30 aspect-square flex items-center justify-center">
                          {f.type.startsWith('video/') ? (
                            <div className="flex flex-col items-center gap-1 text-muted-foreground p-2">
                              <Video className="w-6 h-6" />
                              <span className="text-[10px] truncate max-w-full">{f.name}</span>
                            </div>
                          ) : (
                            <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                          )}
                          <button type="button" onClick={() => removeMedia(i)} className="absolute top-1 end-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-media-${i}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {mediaFiles.length < MAX_FILES && (
                    <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-brand/50 hover:bg-muted/20 transition-colors" data-testid="wr-media-upload-area">
                      <Camera className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{t('workRequest.addPhotosVideos')}</span>
                      <input type="file" className="hidden" accept={ACCEPTED_TYPES} multiple onChange={handleMediaSelect} data-testid="wr-media-input" />
                    </label>
                  )}
                  {mediaFiles.length === 0 && (
                    <p className="text-xs text-red-500">{t('workRequest.mediaRequiredHint')}</p>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setMediaFiles([]); }}>{t('common.cancel')}</Button>
                  <Button type="submit" disabled={submitting || mediaFiles.length === 0} className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="wr-submit-btn">
                    {submitting ? (uploadProgress || t('common.loading')) : t('common.create')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-muted/50 p-1 w-full sm:w-auto flex overflow-x-auto gap-1 no-scrollbar">
          {TABS.map(tab => (
            <TabsTrigger 
              key={tab.key} 
              value={tab.key}
              className="data-[state=active]:bg-card data-[state=active]:text-brand data-[state=active]:shadow-sm"
              data-testid={`tab-${tab.key}`}
            >
              {t(`workRequest.tab.${tab.key}`, tab.key === 'all' ? t('common.all') : tab.key)}
              <Badge variant="secondary" className="ms-2 text-xs">{tabCounts[tab.key] || 0}</Badge>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Quick Filter Chips */}
      <FilterChips
        chips={filterChips}
        activeChips={activeChips}
        onToggle={(chip) => chip.isActive() ? chip.onDeactivate() : chip.onActivate()}
      />

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('common.search')}
            className="ps-9"
            data-testid="wr-search"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <Select value={fBranch} onValueChange={setFBranch}>
          <SelectTrigger className="w-full sm:w-[160px]" data-testid="wr-filter-branch"><SelectValue placeholder={t('common.branch')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {!['open', 'closed'].includes(activeTab) && (
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="w-full sm:w-[150px]" data-testid="wr-filter-status"><SelectValue placeholder={t('common.status')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="NEW">{t('status.NEW')}</SelectItem>
              <SelectItem value="UNDER_REVIEW">{t('status.UNDER_REVIEW')}</SelectItem>
              <SelectItem value="CONVERTED_TO_WO">{t('status.CONVERTED_TO_WO')}</SelectItem>
              <SelectItem value="REJECTED">{t('status.REJECTED')}</SelectItem>
              <SelectItem value="CLOSED">{t('status.CLOSED')}</SelectItem>
            </SelectContent>
          </Select>
        )}

        {activeTab !== 'highPriority' && (
          <Select value={fPriority} onValueChange={setFPriority}>
            <SelectTrigger className="w-full sm:w-[130px]" data-testid="wr-filter-priority"><SelectValue placeholder={t('workRequest.priority')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="LOW">{t('workRequest.low')}</SelectItem>
              <SelectItem value="MEDIUM">{t('workRequest.medium')}</SelectItem>
              <SelectItem value="HIGH">{t('workRequest.high')}</SelectItem>
              <SelectItem value="CRITICAL">{t('workRequest.critical')}</SelectItem>
            </SelectContent>
          </Select>
        )}

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
            <X className="w-4 h-4 me-1" /> {t('filters.resetFilters')}
          </Button>
        )}
      </div>

      {/* Table */}
      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-0">
          <EnhancedTable stickyHeader>
            <StickyTableHeader>
              <tr className="border-b border-border">
                <SortableHeader field="id" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  ID
                </SortableHeader>
                <TableHeader>{t('myMaintenance.woNo')}</TableHeader>
                <SortableHeader field="title" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('common.name')}
                </SortableHeader>
                <TableHeader>{t('common.branch')}</TableHeader>
                <TableHeader>{t('common.room')}</TableHeader>
                <SortableHeader field="priority" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('workRequest.priority')}
                </SortableHeader>
                <SortableHeader field="status" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('common.status')}
                </SortableHeader>
                <TableHeader>{t('workOrder.assignedTo')}</TableHeader>
                <SortableHeader field="created_at" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('common.createdAt')}
                </SortableHeader>
              </tr>
            </StickyTableHeader>
            <EnhancedTableBody>
              {filteredRequests.length === 0 && (
                <TableEmptyState
                  icon={FileText}
                  title={t('workRequest.noRequests')}
                  description={t('workRequest.createFirst')}
                  colSpan={9}
                />
              )}
              {filteredRequests.map(wr => (
                <EnhancedTableRow
                  key={wr.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/work-requests/${wr.id}`)}
                  data-testid={`wr-row-${wr.id}`}
                >
                  <EnhancedTableCell className="font-mono text-xs text-muted-foreground">{wr.id.slice(0, 8)}</EnhancedTableCell>
                  <EnhancedTableCell className="font-mono text-xs text-brand" data-testid={`wr-wo-${wr.id}`}>
                    {wr.work_order_id ? wr.work_order_id.slice(0, 8) : '—'}
                  </EnhancedTableCell>
                  <EnhancedTableCell className="text-foreground font-medium max-w-[200px] truncate">{wr.title}</EnhancedTableCell>
                  <EnhancedTableCell className="text-muted-foreground">{wr.branch_name}</EnhancedTableCell>
                  <EnhancedTableCell className="text-foreground whitespace-nowrap" data-testid={`wr-room-${wr.id}`}>
                    {wr.room_name || <span className="text-muted-foreground italic">{t('common.noRoom')}</span>}
                  </EnhancedTableCell>
                  <EnhancedTableCell><PriorityBadge priority={wr.priority} /></EnhancedTableCell>
                  <EnhancedTableCell><StatusBadge status={wr.wo_status || wr.status} /></EnhancedTableCell>
                  <EnhancedTableCell className="text-muted-foreground whitespace-nowrap">
                    {wr.wo_assigned_to_name || '—'}
                  </EnhancedTableCell>
                  <EnhancedTableCell className="text-muted-foreground text-xs">{formatDate(wr.created_at)}</EnhancedTableCell>
                </EnhancedTableRow>
              ))}
            </EnhancedTableBody>
          </EnhancedTable>
        </CardContent>
      </Card>
    </div>
  );
}
